import React, { useCallback, useMemo, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import {
  AddOutlined as AddIcon,
  DeleteOutlined as DeleteIcon,
  EventOutlined as EventIcon,
} from "@mui/icons-material";
import Button from "../../../common/inputs/Button";
import Section from "../../../layout/Section";
import BaseTable, {
  TableColumn,
  TableData,
} from "../../../tables/common/BaseTable";
import DialogTemplate from "../../../layout/DialogTemplate";
import ActionsMenu from "../../../common/overlays/ActionsMenu";
import ConfirmDialog from "../../../common/feedback/ConfirmDialog";
import VerfahrenFormular from "./VerfahrenFormular";
import ProcedureTypeTiles from "./ProcedureTypeTiles";
import { AntragFeldWerte } from "./AntragFelder";
import AntragStammdaten from "./AntragStammdaten";
import { OrgUser } from "../../../../services/api/users";
import { useToast } from "../../../../contexts/ToastContext";
import { colors, fonts } from "../../../../styles/theme";
import { deletedDeadlinesNotice } from "../../../../utils/deadlines";
import {
  AntragVorbelegung,
  AnlageOption,
  LookupOption,
  PermitProcedure,
  PermitProcedureCreate,
  PermitProcedureUpdate,
  ProcedureAction,
  ProcedureTypeOption,
  StandortmerkmalOption,
  WindenergiegebietOption,
  UvpStatusOption,
} from "../../../../types";

interface PermitProceduresSectionProps {
  procedures: PermitProcedure[];
  /** Der geöffnete Antrag; null zeigt die Liste */
  selectedProcedure: PermitProcedure | null;
  procedureTypeOptions: ProcedureTypeOption[];
  uvpStatusOptions: UvpStatusOption[];
  vorhabenbestandteilOptions: LookupOption[];
  /** Die Anlagen des Projekts – Auswahl für den Antragsgegenstand */
  anlageOptionen: AnlageOption[];
  windenergiegebietOptions: WindenergiegebietOption[];
  /** Benutzer des Mandanten – Auswahl für die Bearbeitung des Antrags */
  orgUsers: OrgUser[];
  standortmerkmalOptions: StandortmerkmalOption[];
  /** Womit das Anlegeformular startet – vom Server (Regel `regeln/vorbelegung_antrag.py`) */
  vorbelegung: AntragVorbelegung | null;
  /** UVP-Status für eine geänderte Anlagenauswahl, nach derselben Regel */
  uvpVorbelegungFuer: (anlagenzahl: number) => Promise<string | null>;
  canEdit: boolean;
  onSelect: (procedureId: string) => void;
  /** Der Antrag ist angelegt – danach öffnet er auf seiner Seite "Nachweise" */
  onAnlageAbgeschlossen: (procedure: PermitProcedure) => void;
  onCreate: (data: PermitProcedureCreate) => Promise<PermitProcedure>;
  /** Speichert einzelne Felder; meldet Erfolg und Fehler selbst über PageTemplate */
  onUpdate: (procedureId: string, data: PermitProcedureUpdate) => Promise<void>;
  onDelete: (procedureId: string) => Promise<void>;
  /** Öffnet den Dialog zu einer Aktion am geöffneten Antrag */
  onAction: (procedure: PermitProcedure, action: ProcedureAction) => void;
  /** Öffnet „Termin anlegen“ mit dem Antrag als Bezug */
  onAddDeadline?: (procedureId: string) => void;
  /** Wie viele manuelle Termine mit dem Antrag gelöscht würden – für die Rückfrage */
  manualDeadlineCount?: (procedureId: string) => number;
  /** IAM-ID des angemeldeten Benutzers – Vorbelegung der Bearbeitung beim Anlegen */
  aktuellerBenutzerId?: string;
  projectId?: string;
}

/**
 * Erster Abschnitt der Seiten "Anträge" und "Antrag" – drei Zustände in einer Kachel:
 *
 * - **kein Antrag**: das Anlegeformular steht direkt in der Kachel ("Neuer Antrag")
 * - **Antrag geöffnet** (Seite Antrag): seine Stammdaten ("Stammdaten")
 * - **kein Antrag geöffnet** (Anträge): die Liste aller Anträge, ohne Titel – die Seite
 *   heißt schon so
 *
 * Zurück zur Liste geht es über die Sidebar. Geändert wird ausschließlich in den Stammdaten;
 * die Liste öffnet, legt an und löscht.
 */
const PermitProceduresSection: React.FC<PermitProceduresSectionProps> = ({
  procedures,
  selectedProcedure,
  procedureTypeOptions,
  uvpStatusOptions,
  vorhabenbestandteilOptions,
  anlageOptionen,
  windenergiegebietOptions,
  orgUsers,
  standortmerkmalOptions,
  vorbelegung,
  uvpVorbelegungFuer,
  canEdit,
  onSelect,
  onAnlageAbgeschlossen,
  onCreate,
  onUpdate,
  onDelete,
  onAction,
  onAddDeadline,
  manualDeadlineCount,
  aktuellerBenutzerId,
  projectId,
}) => {
  const [starterOpen, setStarterOpen] = useState(false);
  /** Die im ersten Schritt gewählte Antragsart; null zeigt die Kacheln */
  const [chosenType, setChosenType] = useState<ProcedureTypeOption | null>(
    null,
  );
  const [deleting, setDeleting] = useState<PermitProcedure | null>(null);
  const { showError } = useToast();
  const isEmpty = procedures.length === 0;

  const handleCreate = useCallback(
    async (werte: AntragFeldWerte) => {
      const created = await onCreate({
        bezeichnung: werte.bezeichnung,
        beschreibung: werte.beschreibung,
        procedure_type_id: werte.procedureTypeId,
        uvp_status_id: werte.uvpStatusId || null,
        vorhabenbestandteil_ids: werte.vorhabenbestandteilIds,
        anlage_ids: werte.anlageIds,
        bearbeitung_user_id: werte.bearbeitungUserId || null,
        bezugsgenehmigung_id: werte.bezugsgenehmigungId || null,
        genehmigung_geplant_am: werte.genehmigungGeplantAm || null,
        conference_planned_on: werte.conferencePlannedOn || null,
        windenergiegebiet_id: werte.windenergiegebietId || null,
        standortmerkmal_ids: werte.standortmerkmalIds,
      });
      setStarterOpen(false);
      setChosenType(null);
      // Die Basisnachweise legt das Backend mit an; geprüft und verteilt werden sie direkt auf
      // der Seite "Nachweise" des neuen Antrags.
      onAnlageAbgeschlossen(created);
    },
    [onCreate, onAnlageAbgeschlossen],
  );

  const typeById = useMemo(
    () => new Map(procedureTypeOptions.map((option) => [option.id, option])),
    [procedureTypeOptions],
  );

  /** Kurzname der Antragsart für die Liste; fehlt er, steht der volle ELiA-Wortlaut da */
  const typeShortName = useCallback(
    (procedure: PermitProcedure): string => {
      const type =
        typeById.get(procedure.procedure_type_id) ?? procedure.procedure_type;
      return type?.kurzname || type?.name || "";
    },
    [typeById],
  );

  const columns: TableColumn[] = useMemo(
    () => [
      {
        key: "bezeichnung",
        label: "Bezeichnung",
        align: "left",
        width: "33.33%",
        sortable: true,
      },
      {
        key: "procedure_type_id",
        label: "Antragsart",
        align: "left",
        width: "33.34%",
        sortable: true,
        sortAccessor: (row) => typeShortName(row as PermitProcedure),
      },
      {
        key: "beschreibung",
        label: "Beschreibung",
        align: "left",
        width: "33.33%",
        sortable: true,
      },
    ],
    [typeShortName],
  );

  const renderCell = useCallback(
    (column: TableColumn, row: TableData) => {
      const procedure = row as PermitProcedure;
      switch (column.key) {
        case "bezeichnung":
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: fonts.weights.medium,
                color: colors.text.primary,
              }}
            >
              {procedure.bezeichnung || "(ohne Bezeichnung)"}
            </Typography>
          );
        case "procedure_type_id": {
          // Kurzname in der Zeile, der volle ELiA-Wortlaut beim Überfahren – ohne Kurznamen
          // stünde im Tooltip nur dasselbe noch einmal
          const shortName = typeShortName(procedure);
          const fullName =
            typeById.get(procedure.procedure_type_id)?.name ??
            procedure.procedure_type?.name ??
            "";
          return (
            <Tooltip
              title={fullName !== shortName ? fullName : ""}
              disableInteractive
            >
              <Typography
                variant="body2"
                sx={{ color: colors.text.secondary, width: "fit-content" }}
              >
                {shortName || "–"}
              </Typography>
            </Tooltip>
          );
        }
        case "beschreibung":
          // Höchstens zwei Zeilen, danach Ellipse – bewusst ohne Tooltip
          return (
            <Typography
              variant="body2"
              sx={{
                color: colors.text.secondary,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {procedure.beschreibung || "–"}
            </Typography>
          );
        default:
          return null;
      }
    },
    [typeById, typeShortName],
  );

  const handleDelete = useCallback(
    async (row: TableData) => {
      try {
        await onDelete(row.id);
      } catch (err) {
        showError(
          err instanceof Error
            ? err.message
            : "Antrag konnte nicht gelöscht werden",
        );
      }
    },
    [onDelete, showError],
  );

  /** Zeilenende: „Termin anlegen“, nach dem Strich „Antrag löschen“ */
  const renderActions = useCallback(
    (row: TableData) => {
      const procedure = row as PermitProcedure;
      return (
        <ActionsMenu
          ariaLabel={`Aktionen zu Antrag ${procedure.bezeichnung || "(ohne Bezeichnung)"}`}
          items={[
            ...(onAddDeadline
              ? [
                  {
                    label: "Termin anlegen",
                    icon: <EventIcon fontSize="small" />,
                    onClick: () => onAddDeadline(procedure.id),
                  },
                ]
              : []),
            {
              label: "Antrag löschen",
              icon: <DeleteIcon fontSize="small" />,
              onClick: () => setDeleting(procedure),
              dividerBefore: !!onAddDeadline,
              danger: true,
            },
          ]}
        />
      );
    },
    [onAddDeadline],
  );

  const deleteMessage = (procedure: PermitProcedure): string =>
    [
      `Antrag "${procedure.bezeichnung || "(ohne Bezeichnung)"}" mit allen Antragsbausteinen, Nachweisen und Kosten wirklich löschen?`,
      deletedDeadlinesNotice(manualDeadlineCount?.(procedure.id) ?? 0),
    ]
      .filter(Boolean)
      .join(" ");

  // Speichert einzelne Felder der Stammdaten (Blur-Pattern, kein Speichern-Knopf). Erfolg und
  // Fehler meldet der Hook über PageTemplate als Toast – hier bleibt nichts abzufangen.
  const handleFieldChange = useCallback(
    (data: PermitProcedureUpdate) => {
      if (!selectedProcedure) return;
      onUpdate(selectedProcedure.id, data);
    },
    [selectedProcedure, onUpdate],
  );

  const closeStarter = () => {
    setStarterOpen(false);
    setChosenType(null);
  };

  /**
   * Anlegen in zwei Schritten, in beiden Auftritten gleich (ohne Antrag direkt in der Kachel,
   * mit Antrag im Dialog hinter "Antrag anlegen"): erst die Antragsart als Kachel, dann das
   * Formular. Die Antragsart steht danach nur noch im Titel; ein Zurück gibt es nicht –
   * Abbrechen und neu anlegen genügt.
   */
  const starterContent = (imDialog: boolean) =>
    chosenType ? (
      <VerfahrenFormular
        // Neue Antragsart, neues Formular – sonst blieben Eingaben des vorigen Anlaufs stehen
        key={chosenType.id}
        procedureTypeId={chosenType.id}
        disabled={!canEdit}
        procedureTypeOptions={procedureTypeOptions}
        uvpStatusOptions={uvpStatusOptions}
        vorhabenbestandteilOptions={vorhabenbestandteilOptions}
        anlageOptionen={anlageOptionen}
        windenergiegebietOptions={windenergiegebietOptions}
        standortmerkmalOptions={standortmerkmalOptions}
        orgUsers={orgUsers}
        aktuellerBenutzerId={aktuellerBenutzerId}
        projectId={projectId}
        vorbelegung={vorbelegung}
        uvpVorbelegungFuer={uvpVorbelegungFuer}
        onSubmit={handleCreate}
        onCancel={imDialog ? closeStarter : () => setChosenType(null)}
      />
    ) : (
      <ProcedureTypeTiles
        options={procedureTypeOptions}
        onSelect={setChosenType}
      />
    );

  /** Titel des zweiten Schritts: Kurzname, darunter der volle ELiA-Wortlaut */
  const chosenTitle = chosenType?.kurzname || chosenType?.name;
  const chosenSubtitle = chosenType?.kurzname ? chosenType.name : undefined;

  const anlegenButton = canEdit ? (
    // Hauptaktion: gefüllt (components/CLAUDE.md, „Primäre und sekundäre Knöpfe“)
    <Button
      variant="primary"
      startIcon={<AddIcon />}
      onClick={() => setStarterOpen(true)}
    >
      Antrag anlegen
    </Button>
  ) : undefined;

  const anlegenDialog = (
    <DialogTemplate
      open={starterOpen}
      onClose={closeStarter}
      title={chosenTitle ? `${chosenTitle} anlegen` : "Antrag anlegen"}
      subtitle={chosenSubtitle}
      maxWidth="md"
    >
      {starterContent(true)}
    </DialogTemplate>
  );

  // Eigene keys je Zustand: ohne sie hält React dieselbe Section-Instanz über den Wechsel
  // hinweg – der aufgeklappte Zustand des Leerzustands bliebe dann am Detail hängen und
  // defaultExpanded={false} liefe ins Leere.
  if (isEmpty) {
    return (
      <Section key="antrag-anlegen" title="Neuer Antrag">
        {chosenType && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6">{chosenTitle}</Typography>
            {chosenSubtitle && (
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                {chosenSubtitle}
              </Typography>
            )}
          </Box>
        )}
        {starterContent(false)}
      </Section>
    );
  }

  if (selectedProcedure) {
    return (
      <Section key="antrag-detail" title="Stammdaten">
        <AntragStammdaten
          // Antragswechsel setzt das Formular zurück – ohne key hielte es den alten Stand
          key={selectedProcedure.id}
          procedure={selectedProcedure}
          procedureTypeOptions={procedureTypeOptions}
          uvpStatusOptions={uvpStatusOptions}
          vorhabenbestandteilOptions={vorhabenbestandteilOptions}
          anlageOptionen={anlageOptionen}
          windenergiegebietOptions={windenergiegebietOptions}
          standortmerkmalOptions={standortmerkmalOptions}
          canEdit={canEdit}
          orgUsers={orgUsers}
          onFieldChange={handleFieldChange}
          onAction={(action) => onAction(selectedProcedure, action)}
        />
      </Section>
    );
  }

  return (
    // Ohne Titel: Die Seite heißt schon „Anträge" (Titel im Seitenkopf).
    <Section key="antrag-liste" noPadding actions={anlegenButton}>
      {anlegenDialog}
      <BaseTable
        columns={columns}
        data={procedures}
        renderCell={renderCell}
        onRowClick={(row) => onSelect(row.id)}
        renderActions={canEdit ? renderActions : undefined}
        actionsColumnWidth="5%"
        disablePagination
        emptyMessage="Noch kein Antrag angelegt"
      />
      <ConfirmDialog
        open={!!deleting}
        title="Antrag löschen"
        message={deleting ? deleteMessage(deleting) : ""}
        confirmLabel="Löschen"
        onConfirm={() => {
          if (deleting) handleDelete(deleting);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </Section>
  );
};

export default PermitProceduresSection;
